import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { FormsModule } from '@angular/forms';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { rsql, rsqlTextSearch } from '../../../shared/helpers/rsql';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import {
  VEHICLE_STATUS_META,
  Vehicle,
  VehicleStatus,
} from '../../../shared/types/vehicle.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocaleService } from '../../../shared/services/locale.service';

const VEHICLES_SEARCH_FIELDS = [
  'licencePlate',
  'make.name',
  'model.name',
  'location',
] as const;

const STATUS_OPTIONS: PrettySelectOption<VehicleStatus | null>[] = [
  { value: 'AVAILABLE', label: 'Disponible' },
  { value: 'RENTED', label: 'En location' },
  { value: 'UNDER_REVIEW', label: 'En revue' },
  { value: 'PRE_UNDER_REVIEW', label: 'Brouillon' },
  { value: 'UNDER_MAINTENANCE', label: 'En maintenance' },
  { value: 'UNAVAILABLE', label: 'Indisponible' },
];

const PAGE_SIZE_OPTIONS: PrettySelectOption<number>[] = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

@Component({
  selector: 'app-vehicles-list',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterModule, PageHeaderComponent, DataStateComponent, PrettySelectComponent, TPipe],
  templateUrl: './vehicles-list.component.html',
})
export class VehiclesListComponent implements OnInit {
  private readonly locale = inject(LocaleService);
  private readonly vehiclesService = inject(VehiclesService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly vehicles = signal<Vehicle[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);

  readonly searchCtrl = this.fb.control('');
  /** `null` means "no filter applied". */
  readonly statusCtrl = this.fb.control<VehicleStatus | null>(null);

  readonly statusOptions = STATUS_OPTIONS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusMeta = VEHICLE_STATUS_META;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.statusCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const search = rsql.and(
      rsqlTextSearch(VEHICLES_SEARCH_FIELDS, this.searchCtrl.value),
      rsql.eq('status', this.statusCtrl.value),
    );

    this.vehiclesService
      .listMine({
        page: this.page(),
        size: this.size(),
        search: search ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.vehicles.set(res.data ?? []);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.vehicles.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }

  vehicleName(v: Vehicle): string {
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    return [make, model].filter(Boolean).join(' ') || this.locale.t('label.vehicleNum', { id: v.id });
  }
}
